import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Wrapper from '../assets/wrappers/RegisterAndLoginPage';
import { Logo } from '../components';
import { customFetch } from '../utils/custom-fetch';
import { toast } from 'react-toastify';

const STEPS = { EMAIL: 1, PIN: 2, NEW_PASSWORD: 3 };

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const pinRefs = useRef([]);

  // ─── שלב 1: שליחת אימייל ───────────────────────────────────────
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('חובה להזין כתובת אימייל');
      return;
    }
    setLoading(true);
    try {
      await customFetch.post('/auth/forgot-password', { email });
      toast.success('קוד אימות נשלח לאימייל שלך');
      setStep(STEPS.PIN);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  };

  // ─── שלב 2: הזנת PIN ────────────────────────────────────────────
  const handlePinChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...pin];
    next[index] = value;
    setPin(next);
    if (value && index < 5) pinRefs.current[index + 1]?.focus();
  };

  const handlePinKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setPin(pasted.split(''));
      pinRefs.current[5]?.focus();
    }
  };

  const handleVerifyPin = (e) => {
    e.preventDefault();
    const code = pin.join('');
    if (code.length < 6) {
      toast.error('יש להזין את כל 6 הספרות');
      return;
    }
    setStep(STEPS.NEW_PASSWORD);
  };

  // ─── שלב 3: איפוס סיסמה ─────────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('הסיסמה חייבת להכיל לפחות 8 תווים');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('הסיסמאות אינן תואמות');
      return;
    }
    setLoading(true);
    try {
      await customFetch.post('/auth/reset-password', {
        email,
        code: pin.join(''),
        password,
      });
      toast.success('הסיסמה עודכנה בהצלחה!');
      navigate('/login');
    } catch (err) {
      const msg = err?.response?.data?.msg || 'שגיאה באיפוס הסיסמה';
      toast.error(msg);
      if (msg.includes('קוד שגוי') || msg.includes('פג תוקף')) {
        setPin(['', '', '', '', '', '']);
        setStep(STEPS.PIN);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <div className="form">
        <Logo />

        {/* ─── שלב 1: אימייל ─── */}
        {step === STEPS.EMAIL && (
          <form onSubmit={handleSendCode} noValidate>
            <h4 className="form-title">שכחתי סיסמה</h4>
            <p style={{ color: 'var(--text-secondary-color)', marginBottom: '1rem', fontSize: '0.9rem' }}>
              הזן את כתובת האימייל שלך ונשלח לך קוד לאיפוס הסיסמה
            </p>
            <div className="form-row">
              <label htmlFor="email" className="form-label">כתובת אימייל</label>
              <input
                id="email"
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                dir="ltr"
                required
              />
            </div>
            <button type="submit" className="btn btn-block" disabled={loading}
              style={{ marginTop: '1rem' }}>
              {loading ? 'שולח...' : 'שלח קוד לאימייל'}
            </button>
            <p style={{ marginTop: '1rem' }}>
              <Link to="/login" className="member-btn">חזרה לכניסה</Link>
            </p>
          </form>
        )}

        {/* ─── שלב 2: PIN ─── */}
        {step === STEPS.PIN && (
          <form onSubmit={handleVerifyPin} noValidate>
            <h4 className="form-title">הזן קוד אימות</h4>
            <p style={{ color: 'var(--text-secondary-color)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              קוד 6 ספרות נשלח לכתובת <strong>{email}</strong>
              <br />
              הקוד תקף ל-10 דקות
            </p>
            <div
              style={{
                display: 'flex', gap: '0.5rem', justifyContent: 'center',
                marginBottom: '1.5rem', direction: 'ltr',
              }}
              onPaste={handlePinPaste}
            >
              {pin.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (pinRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(e.target.value, i)}
                  onKeyDown={(e) => handlePinKeyDown(e, i)}
                  style={{
                    width: '48px', height: '56px', textAlign: 'center',
                    fontSize: '1.5rem', fontWeight: 'bold',
                    border: '2px solid var(--primary-500)',
                    borderRadius: '6px', outline: 'none',
                    background: 'var(--background-secondary-color)',
                    color: 'var(--text-color)',
                  }}
                />
              ))}
            </div>
            <button type="submit" className="btn btn-block">אמת קוד</button>
            <p style={{ marginTop: '1rem', textAlign: 'center' }}>
              לא קיבלת קוד?{' '}
              <button
                type="button"
                className="member-btn"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                onClick={() => { setStep(STEPS.EMAIL); setPin(['', '', '', '', '', '']); }}
              >
                שלח שוב
              </button>
            </p>
          </form>
        )}

        {/* ─── שלב 3: סיסמה חדשה ─── */}
        {step === STEPS.NEW_PASSWORD && (
          <form onSubmit={handleResetPassword} noValidate>
            <h4 className="form-title">סיסמה חדשה</h4>
            <p style={{ color: 'var(--text-secondary-color)', marginBottom: '1rem', fontSize: '0.9rem' }}>
              בחר סיסמה חדשה לחשבון שלך
            </p>
            <div className="form-row">
              <label htmlFor="password" className="form-label">סיסמה חדשה</label>
              <input
                id="password"
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="לפחות 8 תווים"
                minLength={8}
                required
              />
            </div>
            <div className="form-row">
              <label htmlFor="confirmPassword" className="form-label">אימות סיסמה</label>
              <input
                id="confirmPassword"
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="הזן שוב את הסיסמה"
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-block"
              disabled={loading}
              style={{ marginTop: '1rem' }}
            >
              {loading ? 'מעדכן...' : 'עדכן סיסמה'}
            </button>
          </form>
        )}
      </div>
    </Wrapper>
  );
};

export default ForgotPassword;
